import { useState } from 'react';
import { Box, Button, Alert, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useSnackbar } from 'notistack';
import {
  InvestmentTransactionDTO,
  TransactionType,
  TransactionCategory,
  CreateInvestmentTransactionRequestBody,
  UpdateInvestmentTransactionRequestBody,
  InvestmentType,
} from '@inversor-hub/types';
import { TransactionStats } from '@/features/investment-transactions/TransactionStats';
import { TransactionList } from '@/features/investment-transactions/TransactionList';
import {
  TransactionDialog,
  TransactionDialogMode,
  TransactionFormData,
} from '@/features/investment-transactions/TransactionDialog';
import { TransactionFilters } from '@/features/investment-transactions/TransactionFilters';
import {
  useListInvestmentTransactions,
  useGetInvestmentTransactionStats,
  useCreateInvestmentTransaction,
  useUpdateInvestmentTransaction,
  useDeleteInvestmentTransaction,
  useUploadTransactionDocument,
} from '@/lib/api/investment-transactions';
import { TransactionImportWizard } from '@/features/investment-transactions/TransactionImportWizard';

interface TransactionsTabProps {
  investmentId: string;
  investmentType: InvestmentType;
}

export const TransactionsTab = ({ investmentId, investmentType }: TransactionsTabProps) => {
  const { enqueueSnackbar } = useSnackbar();

  const [isTransactionDialogOpen, setIsTransactionDialogOpen] = useState(false);
  const [isImportWizardOpen, setIsImportWizardOpen] = useState(false);
  // Mode and source transaction are kept after closing so the dialog's exit
  // transition does not flash reset fields.
  const [dialogMode, setDialogMode] = useState<TransactionDialogMode>('create');
  const [selectedTransaction, setSelectedTransaction] = useState<
    InvestmentTransactionDTO | undefined
  >();
  const [transactionFilters, setTransactionFilters] = useState<{
    type?: TransactionType;
    category?: TransactionCategory;
    startDate?: string;
    endDate?: string;
  }>({});

  const { data: transactions, isLoading: isLoadingTransactions } = useListInvestmentTransactions(
    investmentId,
    {
      type: transactionFilters.type,
      startDate: transactionFilters.startDate,
      endDate: transactionFilters.endDate,
      categories: transactionFilters.category ? [transactionFilters.category] : undefined,
    }
  );
  const { data: transactionStats, isLoading: isLoadingStats } = useGetInvestmentTransactionStats(
    investmentId,
    {
      type: transactionFilters.type,
      startDate: transactionFilters.startDate,
      endDate: transactionFilters.endDate,
    }
  );
  const createTransactionMutation = useCreateInvestmentTransaction(investmentId);
  const updateTransactionMutation = useUpdateInvestmentTransaction(investmentId);
  const deleteTransactionMutation = useDeleteInvestmentTransaction(investmentId);
  const uploadDocumentMutation = useUploadTransactionDocument(investmentId);
  // Transaction handlers
  const handleCreateTransaction = () => {
    setDialogMode('create');
    setSelectedTransaction(undefined);
    setIsTransactionDialogOpen(true);
  };

  const handleEditTransaction = (transaction: InvestmentTransactionDTO) => {
    setDialogMode('edit');
    setSelectedTransaction(transaction);
    setIsTransactionDialogOpen(true);
  };

  const handleDuplicateTransaction = (transaction: InvestmentTransactionDTO) => {
    setDialogMode('duplicate');
    setSelectedTransaction(transaction);
    setIsTransactionDialogOpen(true);
  };

  const handleDeleteTransaction = async (transactionId: string) => {
    if (window.confirm('¿Estás seguro de eliminar esta transacción?')) {
      try {
        await deleteTransactionMutation.mutateAsync(transactionId);
        enqueueSnackbar('Transacción eliminada', { variant: 'success' });
      } catch (error) {
        enqueueSnackbar('Error al eliminar la transacción', { variant: 'error' });
      }
    }
  };

  const handleViewDocument = (signedUrl: string) => {
    window.open(signedUrl, '_blank', 'noopener,noreferrer');
  };

  const handleSubmitTransaction = async (data: TransactionFormData, file?: File) => {
    try {
      // Always send lines explicitly so the backend can tell a collapsed split
      // (single line) apart from "leave lines untouched". When not split we send
      // one line covering the full amount, matching how non-split transactions
      // are stored on create.
      const lines = data.split
        ? data.lines.map((line) => ({
            category: line.category as TransactionCategory,
            amount: line.amount!,
          }))
        : [{ category: data.category as TransactionCategory, amount: data.amount! }];
      const apiData = {
        type: data.type,
        category: lines[0].category,
        concept: data.concept,
        amount: data.amount!,
        lines,
        date: data.date,
        paymentMethod: data.paymentMethod,
      };

      if (dialogMode === 'edit' && selectedTransaction) {
        await updateTransactionMutation.mutateAsync({
          transactionId: selectedTransaction.id,
          data: apiData as UpdateInvestmentTransactionRequestBody,
        });
        if (file) {
          await uploadDocumentMutation.mutateAsync({
            transactionId: selectedTransaction.id,
            file,
          });
        }
        enqueueSnackbar('Transacción actualizada', { variant: 'success' });
      } else {
        const created = await createTransactionMutation.mutateAsync(
          apiData as CreateInvestmentTransactionRequestBody
        );
        if (file && created) {
          await uploadDocumentMutation.mutateAsync({
            transactionId: created.id,
            file,
          });
        }
        enqueueSnackbar(
          dialogMode === 'duplicate' ? 'Transacción duplicada' : 'Transacción creada',
          { variant: 'success' }
        );
      }
      setIsTransactionDialogOpen(false);
    } catch (error) {
      enqueueSnackbar('Error al guardar la transacción', { variant: 'error' });
    }
  };

  const handleClearFilters = () => {
    setTransactionFilters({});
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Gastos e Ingresos
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gestiona todas las transacciones financieras de esta inversión
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => setIsImportWizardOpen(true)}
          >
            Importar Excel
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateTransaction}>
            Nueva Transacción
          </Button>
        </Box>
      </Box>

      <Box mb={4}>
        <TransactionStats stats={transactionStats} isLoading={isLoadingStats} />
      </Box>

      <Box mb={2}>
        <TransactionFilters
          type={transactionFilters.type}
          category={transactionFilters.category}
          startDate={transactionFilters.startDate}
          endDate={transactionFilters.endDate}
          onTypeChange={(type) => setTransactionFilters((prev) => ({ ...prev, type }))}
          onCategoryChange={(category) => setTransactionFilters((prev) => ({ ...prev, category }))}
          onStartDateChange={(startDate) =>
            setTransactionFilters((prev) => ({ ...prev, startDate }))
          }
          onEndDateChange={(endDate) => setTransactionFilters((prev) => ({ ...prev, endDate }))}
          onClear={handleClearFilters}
        />
      </Box>

      {!isLoadingTransactions && transactions?.length === 0 ? (
        <Alert severity="info">
          No hay transacciones registradas. Haz clic en &#34;Nueva Transacción&#34; para comenzar.
        </Alert>
      ) : (
        <TransactionList
          transactions={transactions}
          isLoading={isLoadingTransactions}
          onEdit={handleEditTransaction}
          onDuplicate={handleDuplicateTransaction}
          onDelete={handleDeleteTransaction}
          onViewDocument={handleViewDocument}
        />
      )}

      <TransactionDialog
        open={isTransactionDialogOpen}
        onClose={() => setIsTransactionDialogOpen(false)}
        onSubmit={handleSubmitTransaction}
        transaction={selectedTransaction}
        mode={dialogMode}
        isSubmitting={createTransactionMutation.isPending || updateTransactionMutation.isPending}
      />

      <TransactionImportWizard
        open={isImportWizardOpen}
        investmentId={investmentId}
        investmentType={investmentType}
        onClose={() => setIsImportWizardOpen(false)}
        onImported={() => enqueueSnackbar('Importación completada', { variant: 'success' })}
      />
    </Box>
  );
};
